import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Avatar,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';
import { ACCENTS, tint, colorForName } from '../theme/accents';
import { formatINR } from '../utils/currency';
import { MethodIndicator, StatusIndicator } from './Indicators';
import { useDistribution } from '../hooks/useMandalName';
import { useFeatures } from '../hooks/useFeatures';

// A member's whole ledger in one place - payments, penalties and distributions.
// Shared so any table showing a member name can open it, rather than each page
// keeping its own copy.
const MemberDetailDialog = ({ memberId, open, onClose }) => {
  const { item, sentence } = useDistribution();
  const { features } = useFeatures();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !memberId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [userRes, paymentsRes, penaltiesRes, distributionsRes] = await Promise.all([
          axios.get(`/api/users/${memberId}`),
          axios.get(`/api/payments/user/${memberId}`),
          axios.get(`/api/penalties/user/${memberId}`),
          axios.get(`/api/distributions/history/${memberId}`),
        ]);
        if (!cancelled) {
          setDetails({
            user: userRes.data,
            payments: paymentsRes.data,
            penalties: penaltiesRes.data,
            distributions: distributionsRes.data,
          });
        }
      } catch (error) {
        if (!cancelled) setDetails(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [memberId, open]);

  const user = details?.user;
  const accent = colorForName(user?.full_name || '');

  const totalPaid = (details?.payments || [])
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  const penaltiesDue = (details?.penalties || [])
    .filter(p => p.status === 'pending')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  // Off for a group that does no draw - unless it already has draws on record, which are never hidden
  const showDistribution = features.featureSilverDistribution || (details?.distributions || []).length > 0;
  const received = (details?.distributions || [])
    .reduce((sum, d) => sum + Number(d.amount), 0);

  const Stat = ({ label, value, color }) => (
    <Grid item xs={6} sm={3}>
      <Typography variant="overline" display="block">{label}</Typography>
      <Typography className="tabular" variant="h6" sx={{ color }}>{value}</Typography>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar
            sx={{
              bgcolor: tint(accent, 0.2),
              color: accent,
              border: '2px solid',
              borderColor: tint(accent, 0.55),
              fontWeight: 700,
              width: 48,
              height: 48,
            }}
          >
            {(user?.full_name || '?').charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h5">{user?.full_name || 'Member'}</Typography>
            <Typography variant="body2" color="text.secondary">
              @{user?.username}
              {user?.contribution_multiplier != null &&
                ` · ${Number(user.contribution_multiplier)}× share`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        )}

        {!loading && !details && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Could not load this member's records.
          </Typography>
        )}

        {!loading && details && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Stat label="Total paid" value={formatINR(totalPaid)} color={ACCENTS.emerald} />
              <Stat label="Payments" value={details.payments.length} color={ACCENTS.indigo} />
              <Stat label="Penalties due" value={formatINR(penaltiesDue)} color={penaltiesDue > 0 ? ACCENTS.vermilion : ACCENTS.emerald} />
              {showDistribution && (
                <Stat label={item ? `${item} received` : 'Received'} value={formatINR(received)} color={ACCENTS.magenta} />
              )}
            </Grid>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="overline" display="block" sx={{ mb: 1 }}>
              Payment history
            </Typography>
            {details.payments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No payments recorded yet.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>For month</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Paid on</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.payments.map(payment => (
                    <TableRow key={payment.id}>
                      <TableCell>{dayjs(`${payment.payment_month}-01`).format('MMM YYYY')}</TableCell>
                      <TableCell align="right" className="tabular" sx={{ fontWeight: 600 }}>
                        {formatINR(payment.amount)}
                      </TableCell>
                      <TableCell>{dayjs(payment.payment_date).format('D MMM YYYY')}</TableCell>
                      <TableCell>
                        <MethodIndicator method={payment.payment_method} size="small" />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{payment.notes || '—'}</TableCell>
                      <TableCell>
                        <StatusIndicator status={payment.status} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Typography variant="overline" display="block" sx={{ mb: 1 }}>
              Penalties
            </Typography>
            {details.penalties.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                No penalties.
              </Typography>
            ) : (
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.penalties.map(penalty => (
                    <TableRow key={penalty.id}>
                      <TableCell>{dayjs(penalty.penalty_date).format('D MMM YYYY')}</TableCell>
                      <TableCell align="right" className="tabular" sx={{ fontWeight: 600 }}>
                        {formatINR(penalty.amount)}
                      </TableCell>
                      <TableCell>{penalty.reason}</TableCell>
                      <TableCell>
                        <StatusIndicator status={penalty.status} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {showDistribution && (
              <>
                <Typography variant="overline" display="block" sx={{ mb: 1 }}>
                  {sentence}s
                </Typography>
                {details.distributions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Not selected for a distribution yet.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.distributions.map(dist => (
                        <TableRow key={dist.id}>
                          <TableCell>{dayjs(dist.distribution_date).format('D MMM YYYY')}</TableCell>
                          <TableCell align="right" className="tabular" sx={{ fontWeight: 600 }}>
                            {formatINR(dist.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<Close />}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemberDetailDialog;
